/**
 * Query language AST (grammar in `@plannello/core` query/parse.ts).
 * Shared by the filter bar, saved views, REST and MCP.
 */
export const QUERY_FIELDS = [
  "is",
  "has",
  "assignee",
  "reporter",
  "type",
  "status",
  "priority",
  "product",
  "space",
  "label",
  "parent",
  "milestone",
  "key",
  "created",
  "updated",
  "completed",
  "due",
  "start",
] as const;
export type QueryField = (typeof QUERY_FIELDS)[number];

export type CompareOp = "=" | ">" | ">=" | "<" | "<=";

export interface FieldClause {
  kind: "field";
  field: QueryField;
  op: CompareOp;
  /** Comma-separated values are ORed: `status:todo,in-progress`. */
  values: string[];
  negate: boolean;
  raw: string;
}

export interface TextClause {
  kind: "text";
  text: string;
  negate: boolean;
  raw: string;
  /**
   * Set when the word looked like `key:value` with a key that is not a built-in field. The
   * resolver turns it into a label-group (Field) filter when a group has that slug; otherwise it
   * stays free text and the parser's `unknown_field` warning stands.
   */
  fieldRef?: { key: string; values: string[] };
}

export type QueryClause = FieldClause | TextClause;

export const QUERY_SORT_KEYS = ["rank", "priority", "created", "updated", "due", "number", "title"] as const;
/** "group" sorts by a single-select label group (Field); `group` holds the group slug. */
export type QuerySortKey = (typeof QUERY_SORT_KEYS)[number] | "group";
export interface QuerySort {
  key: QuerySortKey;
  dir: "asc" | "desc";
  /** Label group slug, only with key "group" (`sort:phase`). */
  group?: string;
}

export type QueryWarningCode =
  | "unknown_field"
  | "invalid_value"
  | "invalid_operator"
  | "unknown_value"
  | "unsortable_field"
  | "unterminated_quote"
  /** The query text was cut at `MAX_QUERY_CHARS` (parse.ts); `raw` is the dropped tail, shortened. */
  | "query_too_long"
  /** Clauses (or values of one clause) beyond the limit were ignored (parse.ts). */
  | "too_many_clauses";

export interface QueryWarning {
  code: QueryWarningCode;
  raw: string;
  field?: string;
  value?: string;
}

/** Clauses are ANDed. */
export interface QueryAst {
  clauses: QueryClause[];
  sort: QuerySort[];
  warnings: QueryWarning[];
}
